/**
	* Base map layer tool
	* Handles: switching between base maps
	* @module BaseMapLayerTool
*/

/// Imports

// React
import React, {useState, useEffect} from "react";

// OpenLayers
import OlMap from "ol/Map";
import Layer from "ol/layer/Layer";
import Source from 'ol/source/Source';

// Tools
import { getBaseLayers } from "lib/utilities/map";
import { assetURL } from "lib/utilities/asset";

// Models
import MapToolMode from "lib/enums/maptoolmode";

// Lang
import { getLocaleString } from "lib/localization/vendor";

// UI
import BaseLayerPicker from "views/popups/BaseLayerPicker";

// Styles
import "styles/css/BaseMapLayerTool.scss";

interface BaseMapLayerToolProps {
	olMap:OlMap,
	currentToolMode:MapToolMode
}

var currentIndex:number = 0;
var initialized:boolean = false;

export default function BaseMapLayerTool({olMap, currentToolMode}:BaseMapLayerToolProps) {
	const [currentBaseLayer, setCurrentBaseLayer] = useState<any>();
		
	// Initialize the current base layer
	useEffect(() => {
		initialized = false;
		
		// This value in _config can change depending on the URL (handled in includes/ol-hashed/main)
		currentIndex = _config.defaultBaseLayerIndex;
		
		enableBaseLayerAtIndex();
		
		// Enable a certain base layer from anywhere
		_events.on("enable-base-layer", (layer:Layer) => {
			switchBaseLayerTo(layer);
		});
		
		initialized = true;
	}, [olMap]);
	
	/*
		Enable the base layer at the index
	*/
	const enableBaseLayerAtIndex = () => {
		const layers:Layer[] = getBaseLayers(olMap);
		const newLayer:Layer = layers[currentIndex];
		
		newLayer.setVisible(true);
		
		setCurrentBaseLayer(newLayer);
		
		_events.invoke("active-base-layer", newLayer);
		
		// Only invoke this event after the map is initialized
		if(initialized)
			_events.invoke("layer-toggled", newLayer);
	}
	
	/*
		Disable the base layer at the index
	*/
	const disableBaseLayerAtIndex = () => {
		const layers:Layer[] = getBaseLayers(olMap);
		const newLayer:Layer = layers[currentIndex];
		
		newLayer.setVisible(false);
		
		// Only invoke this event after the map is initialized
		if(initialized)
			_events.invoke("layer-toggled", newLayer);
	}
	
	/*
		Switch the base layer by increasing index
	*/
	const switchBaseLayer = () => {
		const layers:Layer[] = getBaseLayers(olMap);
		
		// Only one base layer, ignore this call
		if(layers.length <= 1) return;
		
		disableBaseLayerAtIndex();
		
		if(currentIndex+1 > layers.length-1)
			currentIndex = 0;
		else
			currentIndex++;
		
		enableBaseLayerAtIndex();
	}
	
	/*
		Switch the base layer by increasing index
	*/
	const switchBaseLayerTo = (layer:Layer) => {
		const layers:Layer[] = getBaseLayers(olMap);
		
		disableBaseLayerAtIndex();
		
		currentIndex = layers.indexOf(layer);
		
		enableBaseLayerAtIndex();
	}
	
	/*
		Returns next base layer
	*/
	const getNextBaseLayer = () => {
		const layers:Layer[] = getBaseLayers(olMap);
		
		let nextIndex:number = currentIndex+1;
		
		if(nextIndex > layers.length-1)
			nextIndex = 0;
		
		return layers[nextIndex];
	}
	
	/*
		On menu button clicked
	*/
	const onMenuButtonClicked = (e:any) => {
		e.preventDefault();
		showBaseLayersPopup();
	}
	
	/*
		Show the base layer popup
	*/
	const showBaseLayersPopup = () => {
		BaseLayerPicker.set("map", olMap);
		BaseLayerPicker.set("select-action", switchBaseLayerTo);
		
		_ui.showPopup(BaseLayerPicker);
	}
	
	/*
		Draw the base layer box
	*/
	const drawSelector = () => {
		const currentLayer:any = currentBaseLayer?.get("config");
		const nextLayer:any = getNextBaseLayer()?.get("config");
		const layer:any = currentLayer; //nextLayer; // Either show current base layer or the next one
		
		if(!layer) return;
		
		let scaleClass:string = "";
		
		if(_dimensions.width < 300 || _dimensions.height < 300)
			scaleClass = " base-map-switcher--smallest";
		else
		if(_dimensions.width < 500 || _dimensions.height < 510)
			scaleClass = " base-map-switcher--smaller";
		
		const className:string = `base-map-switcher${scaleClass}`;
		
		return (
			<div id="base-map-switcher" className={className}>
				<div 
					className="base-map-switcher__texture" 
					style={{backgroundImage: `url(${layer.image})`}}
					onClick={() => switchBaseLayer()}
				></div>
				<div 
					className="base-map-switcher__menu"
					data-tip={getLocaleString("baselayers")}
					data-delay-show="700" 
					onClick={onMenuButtonClicked}
				>
					<img src={assetURL("assets/images/menu.png")} />
				</div>
			</div>
		)
	}
	
	return drawSelector();
}